// src/features/planning/pages/CapacityCheckPage.tsx
// ADR-098 Rule CAP-FE-02/03. انتخاب PlanningPeriod → اجرای اکشن run →
// نمایش نتایج CapacityLoadResult با هایلایت ردیف‌های over-capacity.
//
// ⚠️ نیازمند تأیید قبل از merge (ADR-098 Action Item #1):
// 1. useV1PlanningCapacityChecksList / useV1PlanningPeriodsList نام‌های
//    فرضی هستند (الگوی useV1Planning<PathSegment><Action>).
// 2. اکشن سفارشی run (POST detail=False به .../capacity-checks/run/?period=)
//    در orval معمولاً به یک هوک جدا با نام‌گذاری خاص عملیات سفارشی تبدیل
//    می‌شود (چیزی شبیه useV1PlanningCapacityChecksRunCreate) — این نام را
//    حتماً با grep روی v1.ts واقعی تأیید کنید؛ فراخوانی POST خام با
//    axiosInstance به‌عنوان fallback در کامنت پایین فایل آورده شده است.

import { useState } from "react";
import {
  useV1PlanningCapacityChecksList,
  useV1PlanningPeriodsList,
  useV1PlanningCapacityChecksRunCreate, // ⚠️ نام فرضی — verify کنید
} from "@/api/generated/v1/v1";
import { usePermission } from "@/auth/usePermission";
import { extractResults } from "@/shared/utils/apiResponseUtils";
import { PageBanner } from "@/shared/components/PageBanner";
import type { CapacityCheckRun, PlanningPeriod } from "../types";

export function CapacityCheckPage() {
  const canManage = usePermission("planning.manage");

  const { data: periodsData } = useV1PlanningPeriodsList();
  const periods: PlanningPeriod[] = extractResults<PlanningPeriod>(periodsData);

  const { data: runsData, isLoading: runsLoading, refetch } = useV1PlanningCapacityChecksList();
  const runs: CapacityCheckRun[] = extractResults<CapacityCheckRun>(runsData);

  const { mutateAsync: runCheck, isPending } = useV1PlanningCapacityChecksRunCreate();

  const [periodId, setPeriodId] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function handleRun() {
    setError(null);
    if (!periodId) {
      setError("انتخاب بازهٔ زمانی (Period) الزامی است.");
      return;
    }
    try {
      // اگر هوک فرضی بالا با orval واقعی مطابقت نداشت، جایگزین امن:
      // await axiosInstance.post(`/api/v1/planning/capacity-checks/run/?period=${periodId}`);
      await runCheck({ params: { period: periodId } } as any);
      refetch();
    } catch {
      setError("اجرای بررسی ظرفیت با خطا مواجه شد.");
    }
  }

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title="بررسی ظرفیت تولید"
        subtitle="کنترل بار کاری ماشین‌آلات و ایستگاه‌ها در دوره‌های برنامه‌ریزی"
        icon="⚖️"
        helpLink="/help/planning/capacity-check"
      />

      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>

        {canManage && (
          <div className="mb-6 flex gap-3 items-end">
            <label className="flex flex-col text-sm text-muted">
              بازهٔ زمانی
              <select
                className="input-field"
                value={periodId}
                onChange={(e) => setPeriodId(e.target.value)}
              >
                <option value="">— انتخاب کنید —</option>
                {periods.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="btn-primary" onClick={handleRun} disabled={isPending}>
              {isPending ? "در حال اجرا..." : "اجرای بررسی ظرفیت"}
            </button>
          </div>
        )}

        {error && <p style={{ color: "#ef4444" }}>{error}</p>}

        <h2 className="text-lg font-semibold mb-3">اجراهای قبلی</h2>
        {runsLoading ? (
          <p className="text-muted">در حال بارگذاری...</p>
        ) : runs.length === 0 ? (
          <p className="text-muted text-center p-6">هنوز هیچ بررسی ظرفیتی اجرا نشده.</p>
        ) : (
          runs.map((run) => (
            <div key={run.id} className="mb-6 border-b border-white/10 pb-4">
              <p className="text-sm text-muted mb-2">
                اجراشده در: {run.executed_at} — بازه: {run.checked_period}
              </p>
              <table className="w-full text-sm text-left rtl:text-right border-collapse">
                <thead>
                  <tr>
                    <th className="px-3 py-2.5 text-muted font-medium border-b border-white/10">Work Center</th>
                    <th className="px-3 py-2.5 text-muted font-medium border-b border-white/10">نیاز (دقیقه)</th>
                    <th className="px-3 py-2.5 text-muted font-medium border-b border-white/10">ظرفیت (دقیقه)</th>
                    <th className="px-3 py-2.5 text-muted font-medium border-b border-white/10">وضعیت</th>
                  </tr>
                </thead>
                <tbody>
                  {run.results.map((r) => (
                    <tr
                      key={r.id}
                      className={
                        r.is_over_capacity
                          ? "border-b border-white/5 bg-red-500/10"
                          : "border-b border-white/5 hover:bg-white/5 transition-colors"
                      }
                    >
                      <td className="px-3 py-2.5 font-mono text-xs">{r.work_center}</td>
                      <td className="px-3 py-2.5">{r.required_minutes}</td>
                      <td className="px-3 py-2.5">{r.available_minutes}</td>
                      <td className="px-3 py-2.5">
                        {r.is_over_capacity ? (
                          <span style={{ color: "#ef4444" }}>بیش از ظرفیت</span>
                        ) : (
                          <span style={{ color: "#22c55e" }}>در محدودهٔ ظرفیت</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
